import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Calender, CloseCircle, Clock, Location, User, Users } from "../../Theme/Icons";
import { PopupBody, PopupWrapper } from "./CommonPopup";
import { Breaker, Button, Flexbox, InputField, WrapperBox } from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { globalTheme } from "../../Theme/Theme";
import {
  addRequestComment,
  clearRequestComments,
  getRequestComments,
  classroomData,
} from "../../../redux/classroom/classroomSlice";

const DASHBOARD_COMMENT_TYPE = "dashboard";

const initialsFor = (first, last) =>
  `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "U";

// Timeline entries are a record, so they carry the actual moment rather than a
// relative "2 days ago" — an admin reconciling what happened to a booking needs
// the date, not the distance from now.
export const formatActivityTime = (value) =>
  value ? moment(value).format("DD MMM YYYY, hh:mm A") : "";

// A row is EITHER a comment (avatar + author + body) or an `activity` line —
// something that happened to the booking, which reads as a quiet timeline entry.
// Kept in step with the same component in DashboardUser.
const CommentRow = ({ comment }) => {
  const isActivity =
    comment?.type === "activity" || (!comment?.comment && !!comment?.activity);
  const c = comment?.commenter || {};
  const when = formatActivityTime(comment?.created_at);

  // No avatar and no "Activity" heading: the sentence already names the actor,
  // so the label only repeated it. A small dot marks the timeline, in a 32px
  // column so it aligns with the avatars on comment rows.
  if (isActivity) {
    return (
      <Flexbox gap={10} alignStart noWrap>
        <Flexbox
          justiCenter
          style={{ width: 32, minWidth: 32, flexShrink: 0, paddingTop: 6 }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              minWidth: 6,
              borderRadius: "50%",
              background: globalTheme.colors.nuetral6,
              display: "block",
            }}
          />
        </Flexbox>
        <Text
          font12
          inter
          regular
          nuetral6
          style={{ flex: 1, minWidth: 0, wordBreak: "break-word", fontStyle: "italic" }}
        >
          {comment?.activity}
          <span style={{ fontStyle: "normal", opacity: 0.7, whiteSpace: "nowrap" }}>
            {" · "}{when}
          </span>
        </Text>
      </Flexbox>
    );
  }

  return (
    <Flexbox gap={10} alignStart noWrap>
      <Flexbox
        alignCenter
        justiCenter
        style={{
          width: 32,
          height: 32,
          minWidth: 32,
          borderRadius: "50%",
          background: globalTheme.colors.grey8,
          border: `1px solid ${globalTheme.colors.nuetral2}`,
          flexShrink: 0,
        }}
      >
        <Text font11 inter semiBold nuetral8 style={{ lineHeight: 1 }}>
          {initialsFor(c.firstName, c.lastName)}
        </Text>
      </Flexbox>
      <Flexbox column gap={2} alignStart style={{ flex: 1, minWidth: 0 }}>
        <Flexbox gap={8} alignCenter>
          <Text font13 inter semiBold black3>
            {`${c.firstName || ""} ${c.lastName || ""}`.trim() || "User"}
          </Text>
          <Text font11 inter regular nuetral6>{when}</Text>
        </Flexbox>
        <Text font13 inter regular grey2 style={{ wordBreak: "break-word" }}>
          {comment?.comment}
        </Text>
      </Flexbox>
    </Flexbox>
  );
};

// Admin-only comment thread scoped to the Eduspace dashboard calendar. Uses the
// shared request-comment thunks with type "dashboard", so these comments never
// appear on My Bookings / All Bookings and send no notification email.
const DashboardCommentsPanel = ({ requestId }) => {
  const dispatch = useDispatch();
  const {
    requestCommentsList,
    isCommentsFetching,
    isPostingComment,
    commentPostSuccess,
  } = useSelector(classroomData);
  const [commentText, setCommentText] = useState("");

  useEffect(() => {
    if (!requestId) return;
    dispatch(clearRequestComments());
    dispatch(getRequestComments({ requestId, type: DASHBOARD_COMMENT_TYPE }));
    return () => {
      dispatch(clearRequestComments());
    };
  }, [dispatch, requestId]);

  useEffect(() => {
    if (commentPostSuccess) setCommentText("");
  }, [commentPostSuccess]);

  const comments = requestCommentsList || [];

  const handlePost = () => {
    const text = commentText.trim();
    if (!text || !requestId) return;
    dispatch(addRequestComment({ requestId, comment: text, type: DASHBOARD_COMMENT_TYPE }));
  };

  return (
    <WrapperBox
      background={globalTheme.colors.white1}
      borderRadius={14}
      padding={"16px"}
      overflow={"hidden"}
      style={{ marginTop: 14 }}
    >
      <Flexbox alignCenter gap={8} maxWidth="max-content">
        <Text font15 inter semiBold black3>Dashboard Notes</Text>
        <Flexbox
          alignCenter
          justiCenter
          maxWidth="max-content"
          background={globalTheme.colors.grey8}
          borderRadius={50}
          style={{ minWidth: 22, height: 20, padding: "0 7px" }}
        >
          <Text font11 inter semiBold nuetral8>{comments.length}</Text>
        </Flexbox>
      </Flexbox>
      <Text font12 inter regular nuetral6 style={{ marginTop: 4 }}>
        Visible only on the dashboard. Not shared with the requester.
      </Text>

      <Breaker marginTop={12} marginBottom={12} />

      <Flexbox
        column
        gap={14}
        alignStart
        style={{ minHeight: 60, maxHeight: 220, overflowY: "auto" }}
      >
        {isCommentsFetching ? (
          <Flexbox justiCenter alignCenter style={{ width: "100%", padding: "14px 0" }}>
            <Text font13 inter regular grey2>Loading notes…</Text>
          </Flexbox>
        ) : comments.length > 0 ? (
          comments.map((c, i) => <CommentRow key={c.id || i} comment={c} />)
        ) : (
          <Flexbox column gap={4} justiCenter alignCenter style={{ width: "100%", padding: "12px 0" }}>
            <Text font13 inter medium black3>No notes yet</Text>
            <Text font12 inter regular nuetral6>Add an internal note for this booking.</Text>
          </Flexbox>
        )}
      </Flexbox>

      <Breaker marginTop={12} marginBottom={12} />

      <Flexbox column gap={10} alignStart>
        <InputField
          noLabel
          type="textarea"
          rows={2}
          placeholder="Write an internal note…"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
        />
        <Flexbox flexEnd>
          <Button
            buttonMd
            primaryFilled
            primary
            maxWidth="max-content"
            name={isPostingComment ? "Posting…" : "Add Note"}
            disabled={isPostingComment || !commentText.trim()}
            onClick={handlePost}
          />
        </Flexbox>
      </Flexbox>
    </WrapperBox>
  );
};

const InfoRow = ({ icon, children }) => (
  <Flexbox alignCenter gap={8} maxWidth="max-content">
    {icon}
    <Text font13 inter regular black3>
      {children}
    </Text>
  </Flexbox>
);

const VerticalSep = () => (
  <Flexbox
    maxWidth="max-content"
    style={{
      width: 1,
      height: 14,
      background: globalTheme.colors.nuetral3,
      margin: "0 4px",
    }}
  />
);

export const BookingDetailsBody = ({
  onClose,
  onOverride,
  onAccept,
  onCancel,
  isPopupFetching,
  image,
  roomName = "NPS",
  capacity = 60,
  location = "Tower A Second Floor",
  bookingDate = "Thursday, 12th Sept, 2026",
  bookingTime = "10:00 AM - 12:00 PM",
  duration = "2 hours",
  bookedBy = "John Doe",
  description = "Booking UG Annual Showcase Dance Practice.",
  requestId = null,
  colorTypes = [],
  colorTypeId = null,
  onChangeColorType = null,
  isClassAdmin = false,
  // Details stay editable only while the request is under review; once it is
  // approved or rejected `canEdit` goes false and `editLocked` explains why to
  // the people who could otherwise have edited it (owner / admin).
  canEdit = false,
  editLocked = false,
  // A still-pending request built from non-adjacent slots (a gap between
  // them) can't be reopened in this form — it only ever edits one contiguous
  // slot — so `canEdit` is false for a reason distinct from `editLocked`.
  splitLocked = false,
  onEdit,
}) => (
  <>
    <Flexbox spaceBW alignCenter>
      <Text font18 inter semiBold white>
        Booking Details
      </Text>
      <CloseCircle className="pointer" onClick={() => onClose?.()} />
    </Flexbox>

    <Flexbox
      style={{
        height: 1,
        background: globalTheme.colors.white1,
        opacity: 0.18,
        margin: "16px 0",
      }}
    />

    <WrapperBox
      background={globalTheme.colors.white1}
      borderRadius={14}
      padding={"16px"}
      overflow={"hidden"}
    >
      <Flexbox gap={16} alignStart noWrap>
        {image ? (
          <img
            src={image}
            alt={roomName}
            style={{
              width: 140,
              height: 120,
              borderRadius: 10,
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ) : (
          <Flexbox
            maxWidth="max-content"
            alignCenter
            justiCenter
            style={{
              width: 140,
              height: 120,
              borderRadius: 10,
              background: globalTheme.colors.nuetral2,
              flexShrink: 0,
            }}
          >
            <Text font12 inter medium nuetral6>
              Room
            </Text>
          </Flexbox>
        )}

        <Flexbox column gap={10} style={{ flex: 1, minWidth: 0 }}>
          <Flexbox alignCenter gap={6} maxWidth="max-content">
            <Text font13 inter regular black3>Room:</Text>
            <Text font14 inter semiBold black3>{roomName}</Text>
          </Flexbox>

          <Flexbox alignCenter gap={4} wrap>
            <InfoRow
              icon={<Users color={globalTheme.colors.nuetral7} width="14px" height="14px" />}
            >
              Capacity: {capacity}
            </InfoRow>
            <VerticalSep />
            <InfoRow icon={<Location color={globalTheme.colors.nuetral7} />}>
              {location}
            </InfoRow>
          </Flexbox>

          <InfoRow icon={<Calender color={globalTheme.colors.nuetral7} />}>
            <strong>{bookingDate}</strong>
          </InfoRow>

          <InfoRow icon={<Clock color={globalTheme.colors.nuetral7} />}>
            <strong>{bookingTime}</strong> (Duration: {duration})
          </InfoRow>

          <InfoRow icon={<User color={globalTheme.colors.nuetral7} />}>
            Booked by: <strong>{bookedBy}</strong>
          </InfoRow>
        </Flexbox>
      </Flexbox>

      <Flexbox
        style={{
          height: 1,
          background: globalTheme.colors.nuetral2,
          margin: "14px 0",
        }}
      />

      <Flexbox
        padding={"10px 12px"}
        background={globalTheme.colors.nuetral1}
        borderRadius={8}
        alignCenter
        gap={6}
        wrap
      >
        <Text font13 inter semiBold black3>Description:</Text>
        <Text font13 inter regular nuetral8>{description}</Text>
      </Flexbox>

      {/* Admin color tagging — pick the booking type; the calendar recolors. */}
      {requestId && onChangeColorType && colorTypes.length > 0 && (
        <>
          <Flexbox
            style={{ height: 1, background: globalTheme.colors.nuetral2, margin: "14px 0" }}
          />
          <Flexbox column gap={8} alignStart>
            <Text font13 inter semiBold black3>Booking Type</Text>
            <Flexbox gap={8} wrap alignCenter>
              {colorTypes.map((t) => {
                const selected = t.id === colorTypeId;
                return (
                  <Flexbox
                    key={t.id}
                    maxWidth="max-content"
                    alignCenter
                    gap={6}
                    className="pointer"
                    onClick={() => onChangeColorType(selected ? null : t.id)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 20,
                      border: `1.5px solid ${selected ? t.color : globalTheme.colors.nuetral2}`,
                      background: selected ? `${t.color}18` : globalTheme.colors.white1,
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: t.color,
                        flexShrink: 0,
                      }}
                    />
                    <Text font12 inter medium black3>{t.name}</Text>
                  </Flexbox>
                );
              })}
            </Flexbox>
            {colorTypeId && (
              <Text
                font12
                inter
                medium
                className="pointer"
                onClick={() => onChangeColorType(null)}
                style={{ color: globalTheme.colors.nuetral6, textDecoration: "underline" }}
              >
                Clear type
              </Text>
            )}
          </Flexbox>
        </>
      )}
    </WrapperBox>

    {requestId && <DashboardCommentsPanel requestId={requestId} />}

    {editLocked && (
      <Text
        font12
        inter
        regular
        white
        style={{ display: "block", marginTop: 14, textAlign: "center", opacity: 0.75 }}
      >
        This booking has already been reviewed, so its details can no longer be edited.
      </Text>
    )}
    {splitLocked && (
      <Text
        font12
        inter
        regular
        white
        style={{ display: "block", marginTop: 14, textAlign: "center", opacity: 0.75 }}
      >
        This request has a gap between its slots, so it can't be edited here — manage it from the Bookings list.
      </Text>
    )}

    {(isClassAdmin || canEdit) && (
      <Flexbox alignCenter gap={12} justiCenter style={{ marginTop: 20 }}>
        {canEdit && (
          <Button
            buttonMd
            primaryFilled
            name="Edit Details"
            maxWidth="max-content"
            onClick={() => onEdit?.()}
          />
        )}
        {isClassAdmin && (
          <Button
            buttonMd
            redFilled
            name="Cancel Booking"
            isWaiting={isPopupFetching}
            onClick={() => onCancel?.()}
          />
        )}
      </Flexbox>
    )}
  </>
);

const BookingDetailsPopup = ({ showActive, ...rest }) => (
  <PopupWrapper className={`${showActive ? "active" : ""}`}>
    <PopupBody maxWidth={620} padding={24}>
      <BookingDetailsBody {...rest} />
    </PopupBody>
  </PopupWrapper>
);

export default BookingDetailsPopup;
